import React from 'react';
import { ITableDataSource } from 'models/table';
import { colors } from 'theme/colors';
import Grid from '@mui/material/Grid';
import { Checkbox, Typography } from '@mui/material';
import { Chips } from 'components/atoms/Chips';
import Button from 'components/atoms/Button';
import { EButtonVariants } from 'constants/Buttons';
import { IFirearm } from 'models/firearm';
import { useTranslations } from 'hooks/useTranslations';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { ERouteLinks } from 'models/route';
import { EPermission } from 'models/permissions';
import { Link } from 'react-router-dom';

interface ILegalEntityFirearmTableCard {
  data: ITableDataSource;
  selectAction: (p: any) => void;
  selectedFirearms: IFirearm[];
  renderColor: (arg: ITableDataSource) => any;
}

const LegalEntityGroupSearchFirearmTableCard = ({
  data,
  selectAction,
  selectedFirearms,
  renderColor,
}: ILegalEntityFirearmTableCard) => {
  const { t } = useTranslations();

  const isItemSelected = selectedFirearms?.find((item) => item.id === data.id);

  const renderToggleSelectButtonLabel = () => {
    let label: string;
    if (isItemSelected) {
      label = t('searchForLegalEntitysFirearm.unselectFirearm.button');
    } else {
      label = t('searchForLegalEntitysFirearm.selectFirearm.button');
    }
    return label;
  };

  return (
    <Grid
      container
      spacing={2}
      sx={{
        border: `1px solid ${renderColor(data).border}`,
        borderRadius: '4px',
        overflow: 'hidden',
        mt: 1,
        backgroundColor: renderColor(data).background,
      }}
    >
      <Grid sx={{ p: 2 }} size={{ xs: 6 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('searchForLegalEntitysFirearm.serialNumber.column')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.serialNumber)}</Typography>
      </Grid>
      <Grid sx={{ p: 2 }} size={{ xs: 6 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('searchForLegalEntitysFirearm.make.column')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.make)}</Typography>
      </Grid>
      <Grid sx={{ p: 2 }} size={{ xs: 6 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('searchForLegalEntitysFirearm.model.column')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.model)}</Typography>
      </Grid>
      <Grid sx={{ p: 2 }} size={{ xs: 6 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('searchForLegalEntitysFirearm.calibre.column')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.caliber)}</Typography>
      </Grid>
      <Grid sx={{ p: 2 }} size={{ xs: 6 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('searchForLegalEntitysFirearm.owner.column')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.owner)}</Typography>
      </Grid>
      <Grid sx={{ p: 2 }} size={{ xs: 6 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('searchForLegalEntitysFirearm.keeper.column')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.keeper)}</Typography>
      </Grid>
      <Grid sx={{ p: 2 }} size={{ xs: 6 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('searchForLegalEntitysFirearm.manufacturer.column')}
        </Typography>

        <Typography variant="body2">{emptyFieldHolder(data.manufacturer)}</Typography>
      </Grid>
      <Grid sx={{ p: 2 }} size={{ xs: 6 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('searchForLegalEntitysFirearm.state.column')}
        </Typography>
        {data.state ? <Chips text={data.state} variant={renderColor(data).stateField} /> : '-'}
      </Grid>
      <Grid sx={{ p: 2 }} size={{ xs: 6 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('searchForLegalEntitysFirearm.address.column')}
        </Typography>
        {data.address}
      </Grid>
      <Grid sx={{ p: 2 }} size={{ xs: 6 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('searchForLegalEntitysFirearm.gps.column')}
        </Typography>
        {data.gps}
      </Grid>
      <Grid
        container
        size={{ xs: 12 }}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderTop: {
            xs: `1px solid ${colors.primary100}`,
            lg: 'none',
          },
          m: {
            xs: 2,
            lg: 2,
          },
        }}
      >
        <Grid size={{ xs: 12, sm: 'auto' }}>
          <Typography variant="button" sx={{ textTransform: 'uppercase' }}>
            {t('general.actions.text')}
          </Typography>
        </Grid>
        <Grid
          container
          sx={{ mt: 1, rowGap: 1, justifyContent: 'right' }}
          size={{ xs: 12, sm: 'auto' }}
        >
          <Grid
            sx={{
              pr: {
                sm: 2,
                lg: 0,
              },
              textAlign: 'right',
            }}
          >
            <Checkbox
              onClick={() => selectAction(data)}
              checked={!!isItemSelected}
              sx={{
                marginTop: '3px',
                color: colors.gray300,

                '&.Mui-checked': {
                  color: colors.primary500,
                },
                '&.MuiCheckbox-root': {
                  maxWidth: '24px',
                  maxHeight: '24px',
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 'auto' }} sx={{ mt: { xs: 6, sm: 0 } }}>
            <Button
              label={renderToggleSelectButtonLabel()}
              variant={EButtonVariants.outlined}
              onClick={() => selectAction(data)}
              size="small"
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 'auto' }}>
            <Link to={ERouteLinks.ViewRegisteredFirearm.replace(':id', data.id as string)}>
              <Button
                variant={EButtonVariants.outlined}
                tableButton
                label={t('general.details.text')}
                permission={[EPermission.VIEW_FIREARMS]}
                size="small"
                fullWidth
              />
            </Link>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export { LegalEntityGroupSearchFirearmTableCard };
